import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check, tweensAxis } from '../skillUsage';

export const carrierMarkSkill: CodeoSkill = {
    name: SKILL_NAMES.carrierMark,
    kind: 'codeo',
    description:
        'Run one small mark through the whole video (a dot, square, triangle, simple icon, or ' +
        'a character such as $ ! ? ) that hops from word to word on arcs, squashes on every ' +
        'landing and lands where the copy has a place for it, carried across every cut so the ' +
        'beats read as one continuous piece.',
    checkLooksFor: 'one mark moved on both x and y and squashed on scaleX and scaleY as it lands',
    documentUsesSkill: check(
        (script) =>
            /scaleX/.test(script) &&
            /scaleY/.test(script) &&
            tweensAxis(script, 'x') &&
            tweensAxis(script, 'y'),
    ),
    reviewNote:
        'A small mark drawn squashed or stretched, in flight between words or landing beside one, ' +
        'is this technique working.',
    instructions: `# Carrier Mark

One small mark in the hot accent is the video's recurring character. It **hops from beat to beat on arcs**, **squashes on every landing** and springs off again, and wherever it lands it **becomes part of the copy**. Words come and go; the mark stays, and its hops carry the eye from one beat to the next, and from one graphic to the next.

## Choosing the mark

One mark for the whole video, chosen for the subject and for where it can land:

| Mark | Lands as |
|---|---|
| Dot | the full stop after a word, the dot of an i, a bounce on a figure's decimal point |
| Square | a full stop for a blocky face, a bullet before a word |
| Triangle or play symbol | a pointer before the next word, a "go" |
| Lightning | the spark beside a word about speed, power, energy |
| Check | the tick after a result, a promise kept |
| Bookmark, bell | the tag on a word to remember, the alert on news |
| Arrow | the direction a word goes: up beside "rising", forward beside "next" |
| A character: $ € % ! ? ' | the currency sign before a figure, the % after it, the ! or ? after a word, an apostrophe |

**Size**: a shape is drawn about 28% of the word's font-size across, a full stop's size. A character is a letter of the copy, so it is set at **the word's own font-size** (\`size\` returns the font-size itself, not 28% of it): a $ before a figure stands as tall as the digits. Keep icons to a single solid silhouette (the paths below); nothing with detail.

## The rules

- **It lands on the copy.** Every landing is a real position in the type: after the last letter on the baseline, between two figures, before a figure, beside a word. Measure it with \`landOn\`; never guess an offset.
- **Arcs**: \`x\` linear, \`y\` eased out to the apex and in to the landing (\`${SKILL_NAMES.arcPathMotion}\`), 0.3–0.5s a hop.
- **Squash on contact** (about 1.35 × 0.7), then wobble back (\`${SKILL_NAMES.squashAndStretch}\`), about its bottom so it stays on the baseline; a short crouch (1.12 × 0.88) before each take-off.
- **It never stands in for the copy's own punctuation.** A figure keeps its own decimal point, a sentence its own full stop where it needs one; the mark lands on or beside them. Copy that leaves a gap for the mark to fill reads wrong whenever the mark is elsewhere ("3 5" for 3.5).
- **Never parked.** It is in flight or resting as part of the copy. When a beat has no place for it, it hops off the frame and returns for the next landing; it never waits in a corner or beside a word it does not belong to.
- **Same colour throughout**: \`var(--l5-color-2)\`, the hot accent, on every ground. Never a tint or a near-white: the mark has to stand out on the light ground as much as the dark.

## Across graphics

The mark is one element carried through every cut, never redrawn. Declare a handoff with id \`carrier\` from every graphic but the last in \`${TOOL_NAMES.writeDesignSystem}\`. The graphic handing it on marks it \`data-l5-carry="carrier"\` and keeps it on screen through its last frame, landed or mid-hop. The next graphic opens with \`<div data-l5-carry-in="carrier"></div>\` in place of its own mark: it is copied in at exactly that pose, under the same id, and the graphic animates it on from there.

${KINETIC_WORD_SETTING}

## Markup

The mark is a wrapper (\`#carrier\`, which moves and carries the size as \`font-size\`) around the shape (\`.carrier-shape\`, which squashes). In the first graphic, beside the word it lands after:

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center">
  <div id="word" data-l5-allow-literal style="line-height: 0.9; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa);">NOW</div>
</div>
<div id="carrier" data-l5-carry="carrier" data-l5-absolute style="position: absolute; left: 0; top: 0; font-size: 100px; color: var(--l5-color-2, #ff4f1a);">
  <div class="carrier-shape" style="width: 1em; height: 1em; border-radius: 50%; background: currentColor; transform-origin: 50% 100%;"></div>
</div>
\`\`\`

The other shapes, each filling the same 1em box:

\`\`\`html
<div class="carrier-shape" style="width: 1em; height: 1em; background: currentColor; transform-origin: 50% 100%;"></div>
<svg class="carrier-shape" viewBox="0 0 24 24" style="width: 1em; height: 1em; display: block; transform-origin: 50% 100%;"><path fill="currentColor" d="M12 3 L22 21 L2 21 Z"/></svg>
<svg class="carrier-shape" viewBox="0 0 24 24" style="width: 1em; height: 1em; display: block; transform-origin: 50% 100%;"><path fill="currentColor" d="M6 3 L21 12 L6 21 Z"/></svg>
<svg class="carrier-shape" viewBox="0 0 24 24" style="width: 1em; height: 1em; display: block; transform-origin: 50% 100%;"><path fill="currentColor" d="M14 1 L4 14 H11 L9 23 L20 9 H13 Z"/></svg>
<svg class="carrier-shape" viewBox="0 0 24 24" style="width: 1em; height: 1em; display: block; transform-origin: 50% 100%;"><path fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" d="M3.5 12.5 L9.5 18.5 L20.5 5.5"/></svg>
<svg class="carrier-shape" viewBox="0 0 24 24" style="width: 1em; height: 1em; display: block; transform-origin: 50% 100%;"><path fill="currentColor" d="M5 1 H19 V23 L12 17 L5 23 Z"/></svg>
<svg class="carrier-shape" viewBox="0 0 24 24" style="width: 1em; height: 1em; display: block; transform-origin: 50% 100%;"><path fill="currentColor" d="M12 1 C7.6 1 5 4.2 5 8.5 V14 L2.5 18 H21.5 L19 14 V8.5 C19 4.2 16.4 1 12 1 Z M9 20 A3 3 0 0 0 15 20 Z"/></svg>
<svg class="carrier-shape" viewBox="0 0 24 24" style="width: 1em; height: 1em; display: block; transform-origin: 50% 100%;"><path fill="currentColor" d="M1 9 H13 V3 L23 12 L13 21 V15 H1 Z"/></svg>
<div class="carrier-shape" data-l5-allow-literal style="font-family: var(--l5-font-0), sans-serif; font-size: 1em; line-height: 0.8; transform-origin: 50% 100%;">$</div>
\`\`\`

## Timeline code

Positions are canvas points; the helper turns them into translations from wherever the mark is laid out, so the same code works on a fresh mark and on one copied in from the graphic before. Every landing comes from \`landOn\`, which measures the letters themselves, and is taken at the moment the mark touches down, wherever the copy's own animation has it by then. Copy the helpers as they are: they hold for any face, case, line-height and scale, where an offset tuned by eye holds for one.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const carrier = document.querySelector('#carrier');
const shape = carrier.querySelector('.carrier-shape');

const laid = carrier.getBoundingClientRect();
const origin = { x: laid.left - gsap.getProperty(carrier, 'x'), y: laid.top - gsap.getProperty(carrier, 'y') };
// A mark copied in from the graphic before starts where it was left; the first graphic's comes in
// from off the frame.
const start = carrier.hasAttribute('data-l5-carried') ? { x: laid.left, y: laid.top } : { x: -160, y: 720 };

const inkProbe = document.createElement('canvas').getContext('2d');
function fontOf(style) {
  return \`\${style.fontStyle} \${style.fontWeight} \${style.fontSize} \${style.fontFamily}\`;
}

// Every letter of an element that shows, as its ink and the baseline it stands on, in canvas pixels.
// Letters whose ink a clipping container hides (the rest of a digit strip) are left out.
function lettersOf(el) {
  const letters = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let node; (node = walker.nextNode()); ) {
    const style = getComputedStyle(node.parentElement);
    const cased = { uppercase: (c) => c.toUpperCase(), lowercase: (c) => c.toLowerCase() }[style.textTransform] ?? ((c) => c);
    inkProbe.font = fontOf(style);
    for (let i = 0; i < node.data.length; i++) {
      const char = cased(node.data[i]);
      if (!char.trim()) continue;
      const range = document.createRange();
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const box = range.getBoundingClientRect();
      if (!box.height) continue;
      const m = inkProbe.measureText(char);
      const scale = box.height / (m.fontBoundingBoxAscent + m.fontBoundingBoxDescent);
      const baseline = box.top + m.fontBoundingBoxAscent * scale;
      const ink = {
        char,
        baseline,
        fontSize: parseFloat(style.fontSize) * scale,
        left: box.left - m.actualBoundingBoxLeft * scale,
        right: box.left + m.actualBoundingBoxRight * scale,
        top: baseline - m.actualBoundingBoxAscent * scale,
        bottom: baseline + m.actualBoundingBoxDescent * scale,
      };
      if (shows(node.parentElement, ink)) letters.push(ink);
    }
  }
  return letters;
}

function shows(el, box) {
  for (let parent = el; parent && parent !== document.body; parent = parent.parentElement) {
    if (getComputedStyle(parent).overflow === 'visible') continue;
    const clip = parent.getBoundingClientRect();
    if (box.bottom <= clip.top || box.top >= clip.bottom || box.right <= clip.left || box.left >= clip.right) return false;
  }
  return true;
}

// The mark's own ink, in ems of its font-size: a shape fills its 1em box, a character stands on
// its own baseline inside its line.
function markInk() {
  const text = shape.textContent.trim();
  if (!text) return { left: 0, right: 1, baseline: 1 };
  const style = getComputedStyle(shape);
  const fontSize = parseFloat(style.fontSize);
  inkProbe.font = fontOf(style);
  const m = inkProbe.measureText(text);
  const content = m.fontBoundingBoxAscent + m.fontBoundingBoxDescent;
  const line = style.lineHeight === 'normal' ? content : parseFloat(style.lineHeight);
  return {
    left: -m.actualBoundingBoxLeft / fontSize,
    right: m.actualBoundingBoxRight / fontSize,
    baseline: ((line - content) / 2 + m.fontBoundingBoxAscent) / fontSize,
  };
}

// Where the mark's corner goes to set it on an element's copy, at the font-size \`size\`: 'after'
// its last letter on the baseline, 'before' its first, or { on: '.' } standing on that character.
function landOn(el, where, size) {
  const letters = lettersOf(el);
  if (!letters.length) throw new Error(\`landOn: no letter of #\${el.id} shows to land on\`);
  const mark = markInk();
  const gap = letters[letters.length - 1].fontSize * 0.08;
  if (where === 'after') {
    const last = letters[letters.length - 1];
    return { x: last.right + gap - mark.left * size, y: last.baseline - mark.baseline * size };
  }
  if (where === 'before') {
    const first = letters[0];
    return { x: first.left - gap - mark.right * size, y: first.baseline - mark.baseline * size };
  }
  const target = letters.find((letter) => letter.char === where.on);
  if (!target) throw new Error(\`landOn: #\${el.id} shows no "\${where.on}"\`);
  const centre = (target.left + target.right) / 2;
  return { x: centre - ((mark.left + mark.right) / 2) * size, y: target.top - mark.baseline * size };
}

// A landing is measured at the moment the mark touches down, so a word still settling, refitted or
// scaled is met where it is then. settle() measures them all; call it again on every refit.
const landings = [];
function landing(measure) {
  const spot = { measure, at: 0, x: 0, y: 0 };
  landings.push(spot);
  return Object.assign(() => spot, { spot });
}
function settle() {
  for (const spot of landings) {
    tl.seek(spot.at);
    Object.assign(spot, spot.measure());
  }
  tl.seek(0);
  tl.invalidate();
}

// Points and sizes are functions, read when the tween renders, so they follow every settle().
function hop(from, to, at, dur, apex, size) {
  if (to.spot) to.spot.at = at + dur;
  tl.to(shape, { scaleX: 1.12, scaleY: 0.88, duration: 0.05, ease: 'power2.in' }, at - 0.05);
  tl.set(shape, { scaleX: 1, scaleY: 1 }, at);
  tl.fromTo(carrier, { x: () => from().x - origin.x }, { x: () => to().x - origin.x, duration: dur, ease: 'none' }, at);
  tl.to(carrier, { y: () => Math.min(from().y, to().y) - apex - origin.y, duration: dur / 2, ease: 'power2.out' }, at);
  tl.to(carrier, { y: () => to().y - origin.y, duration: dur / 2, ease: 'power2.in' }, at + dur / 2);
  tl.to(carrier, { fontSize: () => size() + 'px', duration: dur, ease: 'sine.inOut' }, at);
  tl.set(shape, { scaleX: 1.35, scaleY: 0.7 }, at + dur);
  tl.to(shape, { scaleX: 1, scaleY: 1, duration: 0.35, ease: 'elastic.out(1, 0.45)' }, at + dur + 0.02);
}

const word = document.querySelector('#word');
const size = () => parseFloat(word.style.fontSize) * 0.28;   // a character mark: the font-size itself
const stop = landing(() => landOn(word, 'after', size()));
hop(() => start, stop, 0.2, 0.42, 260, size);
hop(stop, () => ({ x: stop().x - 700, y: stop().y - 120 }), 1.5, 0.4, 220, () => size() * 0.8);
tl.set({}, {}, 2.2);
fitWord(word, 0.72, settle);
window.__timelines = { main: tl };
\`\`\`

Build every hop before \`fitWord\` runs, so its first fit settles them all. Land on the element whose letters show: the visible figure, not a wrapper around something else. To land between two figures or beside a word, land on one of them and hop from there.

The last hop leaves the mark where the next graphic picks it up: still on screen as this graphic ends.

## Pitfalls

- **Never offset a word's box by hand.** A box is the font's, not the letters': its bottom is not the baseline and its right edge is not the last letter. Every landing comes from \`landOn\`.
- **One mark.** Never two at once, and never a different mark in a later graphic.
- **Never redraw it in the next graphic.** Copy it in with the placeholder; a fresh mark at a guessed position jumps at the cut.
- **On screen at the last frame** of every graphic that hands it on, or there is nothing to copy.`,
};
